import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Video as VideoIcon,
  Play,
  ExternalLink,
  X,
  Maximize2,
  Minimize2,
  ListVideo,
  ChevronDown,
  AlertCircle,
  Volume2,
  VolumeX,
} from 'lucide-react';
import api from '@/lib/api';

// Categories shown in the versions dropdown and (crate mode) the top filter
// chips. Ordering here defines button display order.
const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'official', label: 'Official' },
  { id: 'live', label: 'Live' },
  { id: 'lyric', label: 'Lyric' },
];

// GroovLens teal used for accents.
const ACCENT = 'hsl(var(--primary))';

// Cover art fallback for the "no video found" sidebar entry: shows the track
// cover, the GroovLens brand icon on top, and the "No video found" label.
function NoVideoFallback({ track, small = false }) {
  const cover = track?.album_art || track?.cover_url || null;
  return (
    <div
      className={`relative w-full h-full grid place-items-center bg-black overflow-hidden ${small ? '' : 'rounded-xl'}`}
      data-testid="video-wall-no-video-fallback"
    >
      {cover ? (
        <img
          src={cover}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-black" />
      )}
      <div className="relative z-10 flex flex-col items-center gap-2 px-3 py-4 text-center">
        <img
          src="/icons/groovlens-icon-192.png"
          alt="GroovLens"
          className={small ? 'w-8 h-8 rounded-full' : 'w-16 h-16 rounded-full ring-2 ring-white/20'}
        />
        <div
          className={`font-mono uppercase tracking-widest text-white/80 ${small ? 'text-[9px]' : 'text-xs'}`}
        >
          No video found
        </div>
      </div>
    </div>
  );
}

/**
 * VideoWallModal — GroovLens Video Wall.
 *
 * Modes:
 *   - `single`  — one track. Sidebar shows the versions (official/live/lyric)
 *                 from the versions dropdown, no auto-advance.
 *   - `crate`   — multiple tracks. Sidebar is a YouTube-style playlist. Top
 *                 category filter chips. Auto-advance to next track on end.
 *
 * Props:
 *   open (bool)
 *   onOpenChange (fn)
 *   mode ('single' | 'crate')
 *   track (obj)         — single mode
 *   tracks (array)      — crate mode
 *   crateName (string)  — crate mode header
 */
export default function VideoWallModal({
  open,
  onOpenChange,
  mode = 'single',
  track = null,
  tracks = [],
  crateName = 'Crate',
}) {
  const isCrate = mode === 'crate';
  const list = useMemo(() => {
    if (isCrate) return (tracks || []).filter(Boolean);
    return track ? [track] : [];
  }, [isCrate, tracks, track]);

  // Per-track YouTube payload cache: { [cardId]: { loading, data, error } }
  const [payloads, setPayloads] = useState({});
  // Which track index the player is currently on (crate) or 0 (single).
  const [currentIndex, setCurrentIndex] = useState(0);
  // Which video from that track's `results` is playing (default = top).
  const [currentVideoId, setCurrentVideoId] = useState(null);
  // Filter chip (crate mode): 'all' | 'official' | 'live' | 'lyric'
  const [filter, setFilter] = useState('all');
  const [fullscreen, setFullscreen] = useState(false);
  const [muted, setMuted] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile playlist drawer

  const iframeRef = useRef(null);
  const currentTrack = list[currentIndex] || null;
  const currentPayload = currentTrack ? payloads[currentTrack.id] : null;

  // ---- Reset when the modal opens/closes ----
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setCurrentVideoId(null);
      setFilter('all');
      setFullscreen(false);
      setPayloads({});
    }
  }, [open]);

  // ---- Fetch the currently-viewed track's payload (on-demand only) ----
  const fetchPayload = useCallback(async (trackObj) => {
    if (!trackObj || !trackObj.id) return;
    setPayloads((prev) => {
      if (prev[trackObj.id]) return prev;
      return { ...prev, [trackObj.id]: { loading: true, data: null, error: null } };
    });
    try {
      const { data } = await api.post('/youtube/search/by-card', { card_id: trackObj.id });
      setPayloads((prev) => ({
        ...prev,
        [trackObj.id]: { loading: false, data, error: null },
      }));
    } catch (e) {
      setPayloads((prev) => ({
        ...prev,
        [trackObj.id]: {
          loading: false,
          data: null,
          error: e?.response?.data?.detail || 'Search failed',
        },
      }));
    }
  }, []);

  useEffect(() => {
    if (!open || !currentTrack) return;
    if (!payloads[currentTrack.id]) fetchPayload(currentTrack);
  }, [open, currentTrack, payloads, fetchPayload]);

  // Reset the selected video whenever the current track changes.
  useEffect(() => {
    setCurrentVideoId(null);
  }, [currentIndex]);

  // ---- YouTube IFrame Player message listener for "ended" (auto-advance) ----
  useEffect(() => {
    if (!open || !isCrate) return undefined;
    function handleMsg(ev) {
      // The IFrame player posts JSON strings like {"event":"onStateChange","info":0}
      // when using `enablejsapi=1`. state 0 = ended.
      try {
        const src = ev.origin || '';
        if (!src.includes('youtube.com') && !src.includes('youtube-nocookie.com')) return;
        const data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
        if (data && data.event === 'onStateChange' && data.info === 0) {
          setCurrentIndex((i) => (i + 1 < list.length ? i + 1 : i));
        }
      } catch {
        /* not a JSON message we care about */
      }
    }
    window.addEventListener('message', handleMsg);
    return () => window.removeEventListener('message', handleMsg);
  }, [open, isCrate, list.length]);

  // ---- Keyboard shortcuts (Space=pause/play via YT postMessage, arrows=nav) ----
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.target && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (!isCrate) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex((i) => (i + 1 < list.length ? i + 1 : i));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex((i) => (i > 0 ? i - 1 : 0));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isCrate, list.length]);

  // ---- Which video should play right now? ----
  const activeVideo = useMemo(() => {
    const data = currentPayload?.data;
    if (!data || !data.found) return null;
    // Explicit user pick from versions dropdown.
    if (currentVideoId) {
      return (data.results || []).find((r) => r.id === currentVideoId) || data.top;
    }
    return data.top || (data.results || [])[0] || null;
  }, [currentPayload, currentVideoId]);

  // ---- Sidebar list for CRATE mode. Applies category filter. ----
  const sidebarTracks = useMemo(() => {
    if (!isCrate) return [];
    if (filter === 'all') return list;
    // Filter out tracks whose top result category doesn't match. (Tracks
    // still loading are always shown — the filter is best-effort.)
    return list.filter((t) => {
      const p = payloads[t?.id]?.data;
      if (!p || !p.found) return true;
      return p.top?.category === filter;
    });
  }, [isCrate, list, filter, payloads]);

  // ---- Sidebar list for SINGLE mode. Alternative versions of the SAME track. ----
  const singleVersions = useMemo(() => {
    if (isCrate) return [];
    const data = currentPayload?.data;
    if (!data || !data.found) return [];
    return data.results || [];
  }, [isCrate, currentPayload]);

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

  // ---- Embed URL with autoplay + jsapi + mute state ----
  const embedUrl = activeVideo
    ? `${activeVideo.embed_url}?autoplay=1&enablejsapi=1${muted ? '&mute=1' : ''}&rel=0&modestbranding=1&playsinline=1&origin=${encodeURIComponent(window.location.origin)}`
    : null;

  // ---- Header title ----
  const headerTitle = isCrate ? crateName : (currentTrack?.title || 'Video');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        /* Same modal-in-modal pattern as DiscoverSimilarModal — the
           DialogContent is transparent so the visible surface is the
           .crate-neon-border sandwich below. Fullscreen toggle switches
           the outer size class. */
        className={
          fullscreen
            ? '!bg-transparent !border-0 !p-0 !shadow-none !rounded-2xl w-screen h-screen max-w-none max-h-none overflow-visible !grid-cols-1 !gap-0 [&>button]:hidden'
            : '!bg-transparent !border-0 !p-0 !shadow-none !rounded-2xl max-w-4xl w-[96vw] max-h-[92vh] overflow-visible !grid-cols-1 !gap-0 [&>button]:hidden'
        }
        data-testid="video-wall-modal"
      >
        <div
          className="crate-neon-border rounded-2xl shadow-2xl overflow-hidden"
          style={{ padding: '4px' }}
        >
          <div
            className={`bg-card rounded-[14px] ${fullscreen ? 'h-[calc(100vh-8px)]' : 'max-h-[calc(92vh-8px)]'} overflow-hidden flex flex-col relative`}
          >
            {/* ---------- Header ---------- */}
            <DialogHeader className="px-4 sm:px-5 pt-4 pb-3 border-b border-border flex-none">
              <div className="flex items-center gap-3">
                <ListVideo className="w-5 h-5 text-[hsl(var(--primary))] flex-none" />
                <div className="flex-1 min-w-0">
                  <DialogTitle
                    className="font-display text-base sm:text-lg truncate"
                    data-testid="video-wall-title"
                  >
                    {isCrate ? `${crateName} Video Wall` : headerTitle}
                  </DialogTitle>
                  <DialogDescription className="text-[11px] text-muted-foreground mt-0.5">
                    Powered by YouTube
                  </DialogDescription>
                </div>
                <button
                  type="button"
                  onClick={() => setFullscreen((v) => !v)}
                  aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors duration-150"
                  data-testid="video-wall-fullscreen-toggle"
                >
                  {fullscreen ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  aria-label="Close"
                  className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors duration-150"
                  data-testid="video-wall-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Crate mode: category filter chips. Hidden in single mode. */}
              {isCrate && (
                <div
                  className="flex gap-1.5 mt-3 overflow-x-auto scrollbar-none"
                  data-testid="video-wall-filter-chips"
                >
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setFilter(c.id)}
                      className={`flex-none text-xs px-3 py-1 rounded-full border transition-colors duration-150 ${
                        filter === c.id
                          ? 'bg-[hsl(var(--primary))]/15 border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))]'
                          : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                      }`}
                      data-testid={`video-wall-filter-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </DialogHeader>

            {/* Mobile rotation hint — only shows in portrait on narrow screens. */}
            <div
              className="sm:hidden portrait-only text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--primary))]/80 text-center py-1.5 bg-black/40"
              data-testid="video-wall-rotation-hint"
            >
              Turn phone for a better viewing experience
            </div>

            {/* ---------- Body: player + sidebar/versions ---------- */}
            <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
              {/* Player pane */}
              <div className="relative flex-1 min-h-0 bg-black flex items-center justify-center">
                {currentPayload?.loading && !activeVideo && (
                  <div className="w-full aspect-video max-h-full">
                    <Skeleton className="w-full h-full bg-neutral-900" />
                  </div>
                )}
                {!currentPayload?.loading && !activeVideo && currentPayload?.data && (
                  <div className="w-full aspect-video max-h-full">
                    <NoVideoFallback track={currentTrack} />
                  </div>
                )}
                {activeVideo && (
                  <iframe
                    ref={iframeRef}
                    key={activeVideo.id + String(muted)}
                    src={embedUrl}
                    title={activeVideo.title}
                    className="w-full h-full aspect-video max-h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="video-wall-iframe"
                  />
                )}
                {/* Sidebar toggle for mobile (crate mode with hidden sidebar) */}
                {isCrate && (
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="md:hidden absolute bottom-2 right-2 z-10 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur text-xs text-white border border-white/20 hover:bg-black/90 transition-colors"
                    data-testid="video-wall-open-drawer"
                  >
                    <ListVideo className="w-3.5 h-3.5 inline mr-1" />
                    Playlist ({list.length})
                  </button>
                )}
              </div>

              {/* Right-side sidebar (desktop) — ONLY in crate mode. Single-
                  track mode is player-only; users can still switch versions
                  via the "Versions" dropdown in the footer. */}
              {isCrate && (
                <aside
                  className={`hidden md:flex flex-col ${fullscreen ? 'w-96' : 'w-80'} flex-none border-l border-border overflow-hidden`}
                  data-testid="video-wall-sidebar"
                >
                <div className="px-3 py-2 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground flex-none">
                  {isCrate
                    ? `Playlist · ${sidebarTracks.length} track${sidebarTracks.length === 1 ? '' : 's'}`
                    : `Versions · ${singleVersions.length}`}
                </div>
                <div className="flex-1 overflow-y-auto scrollbar-thin">
                  {isCrate
                    ? sidebarTracks.map((t) => (
                        <CrateSidebarItem
                          key={t.id}
                          track={t}
                          payload={payloads[t.id]}
                          isActive={t.id === currentTrack?.id}
                          onClick={() => {
                            const idx = list.findIndex((x) => x.id === t.id);
                            if (idx >= 0) setCurrentIndex(idx);
                            if (!payloads[t.id]) fetchPayload(t);
                          }}
                        />
                      ))
                    : singleVersions.map((v) => (
                        <VersionSidebarItem
                          key={v.id}
                          video={v}
                          isActive={v.id === (activeVideo?.id || null)}
                          onClick={() => setCurrentVideoId(v.id)}
                        />
                      ))}
                  {currentPayload?.loading &&
                    Array.from({ length: 4 }).map((_, i) => (
                      <div key={`sk-${i}`} className="p-3 border-b border-border/50">
                        <Skeleton className="w-full aspect-video mb-2" />
                        <Skeleton className="h-3 w-3/4 mb-1.5" />
                        <Skeleton className="h-2.5 w-1/2" />
                      </div>
                    ))}
                  {!currentPayload?.loading &&
                    isCrate &&
                    sidebarTracks.length === 0 && (
                      <div className="p-6 text-center text-xs text-muted-foreground">
                        No tracks match this filter.
                      </div>
                    )}
                </div>
              </aside>
              )}
            </div>

            {/* ---------- Footer: title + actions ---------- */}
            <div className="border-t border-border px-4 sm:px-5 py-3 flex-none">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div
                    className="font-display text-sm sm:text-base font-semibold truncate"
                    data-testid="video-wall-active-title"
                  >
                    {activeVideo?.title || (currentTrack ? currentTrack.title : '\u00a0')}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {activeVideo?.channel ||
                      (currentTrack
                        ? [currentTrack.artist, currentTrack.duration]
                            .filter(Boolean)
                            .join(' · ')
                        : '\u00a0')}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-none">
                  <button
                    type="button"
                    onClick={() => setMuted((v) => !v)}
                    aria-label={muted ? 'Unmute' : 'Mute'}
                    className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
                    data-testid="video-wall-mute-toggle"
                  >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>

                  {/* Versions dropdown — always available if we have results */}
                  {currentPayload?.data?.results?.length > 1 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="rounded-full h-8 px-3 text-xs"
                          data-testid="video-wall-versions-trigger"
                        >
                          Versions
                          <ChevronDown className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          Pick a version
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {(currentPayload.data.results || []).map((v) => (
                          <DropdownMenuItem
                            key={v.id}
                            onClick={() => setCurrentVideoId(v.id)}
                            className="flex items-start gap-2 py-2"
                            data-testid={`video-wall-version-${v.id}`}
                          >
                            <div className="w-14 aspect-video rounded overflow-hidden flex-none bg-black">
                              {v.thumbnail && (
                                <img
                                  src={v.thumbnail}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium line-clamp-2 leading-tight">
                                {v.title}
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                <span className="truncate">{v.channel}</span>
                                {v.duration && (
                                  <>
                                    <span>&middot;</span>
                                    <span>{v.duration}</span>
                                  </>
                                )}
                              </div>
                              {v.category && v.category !== 'other' && (
                                <Badge
                                  variant="outline"
                                  className="mt-1 h-4 px-1.5 text-[9px] uppercase tracking-widest"
                                >
                                  {v.category}
                                </Badge>
                              )}
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* Watch on YouTube — escape hatch to their algorithm/history */}
                  {(activeVideo?.watch_url || currentPayload?.data?.search_url) && (
                    <Button
                      size="sm"
                      className="rounded-full h-8 px-3 text-xs bg-[#FF0000] hover:bg-[#CC0000] text-white"
                      onClick={() =>
                        window.open(
                          activeVideo?.watch_url || currentPayload?.data?.search_url,
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                      data-testid="video-wall-watch-on-youtube"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />
                      Watch on YouTube
                    </Button>
                  )}
                </div>
              </div>
              {currentPayload?.error && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-400">
                  <AlertCircle className="w-3 h-3" />
                  <span>{currentPayload.error}</span>
                </div>
              )}
            </div>

            {/* Mobile playlist drawer — bottom sheet with the crate track list */}
            {isCrate && drawerOpen && (
              <div
                className="md:hidden absolute inset-0 z-30 bg-black/70 backdrop-blur"
                onClick={() => setDrawerOpen(false)}
                data-testid="video-wall-mobile-drawer"
              >
                <div
                  className="absolute inset-x-0 bottom-0 max-h-[70%] bg-card rounded-t-2xl border-t border-border overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <div className="font-display text-sm font-semibold">Playlist</div>
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(false)}
                      className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary/70"
                      aria-label="Close playlist"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto scrollbar-thin">
                    {sidebarTracks.map((t) => (
                      <CrateSidebarItem
                        key={t.id}
                        track={t}
                        payload={payloads[t.id]}
                        isActive={t.id === currentTrack?.id}
                        onClick={() => {
                          const idx = list.findIndex((x) => x.id === t.id);
                          if (idx >= 0) setCurrentIndex(idx);
                          if (!payloads[t.id]) fetchPayload(t);
                          setDrawerOpen(false);
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Sidebar row components ----------

function CrateSidebarItem({ track, payload, isActive, onClick }) {
  const data = payload?.data;
  const loading = payload?.loading;
  const top = data?.top;
  const hasVideo = !!top;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex gap-3 items-start p-2.5 border-b border-border/50 text-left transition-colors duration-150 ${
        isActive
          ? 'bg-[hsl(var(--primary))]/10 ring-1 ring-inset ring-[hsl(var(--primary))]/40'
          : 'hover:bg-secondary/40'
      }`}
      data-testid={`video-wall-sidebar-item-${track.id}`}
    >
      <div className="relative w-24 aspect-video rounded overflow-hidden flex-none bg-black">
        {loading && !top && <Skeleton className="w-full h-full" />}
        {!loading && !hasVideo && data && <NoVideoFallback track={track} small />}
        {top?.thumbnail && (
          <img src={top.thumbnail} alt="" className="w-full h-full object-cover" />
        )}
        {top?.duration && (
          <div className="absolute bottom-0.5 right-0.5 text-[9px] px-1 rounded bg-black/80 text-white font-mono">
            {top.duration}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-xs font-medium line-clamp-2 leading-tight ${isActive ? 'text-[hsl(var(--primary))]' : ''}`}
        >
          {track.title || 'Unknown track'}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
          {track.artist || 'Unknown artist'}
        </div>
        {isActive && (
          <div className="text-[9px] uppercase tracking-widest text-[hsl(var(--primary))] mt-1 flex items-center gap-1">
            <Play className="w-2.5 h-2.5 fill-current" />
            Playing
          </div>
        )}
      </div>
    </button>
  );
}

function VersionSidebarItem({ video, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex gap-3 items-start p-2.5 border-b border-border/50 text-left transition-colors duration-150 ${
        isActive
          ? 'bg-[hsl(var(--primary))]/10 ring-1 ring-inset ring-[hsl(var(--primary))]/40'
          : 'hover:bg-secondary/40'
      }`}
      data-testid={`video-wall-version-item-${video.id}`}
    >
      <div className="relative w-24 aspect-video rounded overflow-hidden flex-none bg-black">
        {video.thumbnail && (
          <img src={video.thumbnail} alt="" className="w-full h-full object-cover" />
        )}
        {video.duration && (
          <div className="absolute bottom-0.5 right-0.5 text-[9px] px-1 rounded bg-black/80 text-white font-mono">
            {video.duration}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-xs font-medium line-clamp-2 leading-tight ${isActive ? 'text-[hsl(var(--primary))]' : ''}`}
        >
          {video.title}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{video.channel}</div>
        {video.category && video.category !== 'other' && (
          <Badge variant="outline" className="mt-1 h-4 px-1.5 text-[9px] uppercase tracking-widest">
            {video.category}
          </Badge>
        )}
      </div>
    </button>
  );
}
